// Shared styled-Excel generator for the Reports module.
// Produces the same visual design as the "Client Report" workbook:
// orange title bar -> light-green subtitle row -> green header row -> zebra-striped bordered data rows.

// Palette (ARGB) — kept in sync with the Client Report generator in ReportsPage.
const C = {
  orange: 'FFED7D31',
  green: 'FF70AD47',
  dkGreen: 'FF375623',
  ltGreen: 'FFE2EFDA',
  white: 'FFFFFFFF',
  grey: 'FFF2F2F2',
  bdr: 'FFBFBFBF',
  greenBdr: 'FF4F7F3F',
};

const solid = (argb: string) =>
  ({ type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb } });
const thin = (argb: string) =>
  ({ style: 'thin' as const, color: { argb } });
const allBdr = (argb = C.bdr) =>
  ({ top: thin(argb), left: thin(argb), bottom: thin(argb), right: thin(argb) });

const colLetter = (n: number): string => {
  let r = '';
  while (n > 0) { n--; r = String.fromCharCode(65 + (n % 26)) + r; n = Math.floor(n / 26); }
  return r;
};

const styleHeader = (cell: any) => {
  cell.font = { bold: true, size: 10, color: { argb: C.white }, name: 'Calibri' };
  cell.fill = solid(C.green);
  cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  cell.border = allBdr(C.greenBdr);
};

const styleData = (cell: any, even: boolean, align: 'left' | 'center' | 'right' = 'left') => {
  cell.fill = solid(even ? C.white : C.grey);
  cell.border = allBdr();
  cell.alignment = { horizontal: align, vertical: 'middle' };
  cell.font = { size: 10, name: 'Calibri' };
};

export interface StyledReportOptions {
  fileName: string;
  title: string;                       // orange title bar text
  subtitle?: string;                   // light-green row (e.g. client name / filter context)
  headers: string[];                   // table header row
  rows: (string | number)[][];         // data rows, aligned to headers
  sheetName?: string;
}

// Build a single styled worksheet and trigger a browser download.
export async function downloadStyledReport(opts: StyledReportOptions): Promise<void> {
  const { fileName, title, subtitle, headers, rows, sheetName = 'Report' } = opts;

  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'WMS';
  const ws = wb.addWorksheet(sheetName);

  const numCols = Math.max(headers.length, 1);
  const lastCol = colLetter(numCols);

  // Column widths — narrow Sr No, wide data columns.
  ws.getColumn(1).width = 7;
  for (let c = 2; c <= numCols; c++) ws.getColumn(c).width = 22;

  // Row 1 — orange title bar
  ws.mergeCells(`A1:${lastCol}1`);
  ws.getRow(1).height = 30;
  const t1 = ws.getCell('A1');
  t1.value = title;
  t1.font = { bold: true, size: 14, color: { argb: C.white }, name: 'Calibri' };
  t1.fill = solid(C.orange);
  t1.alignment = { horizontal: 'center', vertical: 'middle' };

  // Row 2 — subtitle (client / filter context)
  ws.mergeCells(`A2:${lastCol}2`);
  ws.getRow(2).height = 22;
  const t2 = ws.getCell('A2');
  t2.value = subtitle || '';
  t2.font = { bold: true, size: 12, color: { argb: C.dkGreen }, name: 'Calibri' };
  t2.fill = solid(C.ltGreen);
  t2.alignment = { horizontal: 'center', vertical: 'middle' };

  // Row 3 — table headers (green)
  ws.getRow(3).height = 24;
  headers.forEach((h, ci) => {
    const cell = ws.getRow(3).getCell(ci + 1);
    cell.value = h;
    styleHeader(cell);
  });

  // Rows 4+ — zebra-striped data
  rows.forEach((rowData, idx) => {
    const row = ws.getRow(4 + idx);
    row.height = 16;
    rowData.forEach((v, ci) => {
      const cell = row.getCell(ci + 1);
      cell.value = v as any;
      styleData(cell, idx % 2 === 0, ci === 0 ? 'center' : 'left');
    });
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// A different fixed template (single merged title row + one yellow header
// band + plain bordered data rows, no zebra) — matches the Product/Batch
// master-data export format, distinct from the Client Report style above.
const yellow = 'FFFFFF00';
const yellowBdr = 'FFBFA100';

export interface StyledProductExportOptions {
  fileName: string;
  title: string;                       // merged title row (row 1)
  headers: string[];
  rows: (string | number)[][];
  sheetName?: string;
}

export async function downloadStyledProductExport(opts: StyledProductExportOptions): Promise<void> {
  const { fileName, title, headers, rows, sheetName = 'Products' } = opts;

  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'WMS';
  const ws = wb.addWorksheet(sheetName);

  const numCols = Math.max(headers.length, 1);
  const lastCol = colLetter(numCols);

  for (let c = 1; c <= numCols; c++) ws.getColumn(c).width = 18;

  // Row 1 — merged title (client name)
  ws.mergeCells(`A1:${lastCol}1`);
  ws.getRow(1).height = 24;
  const t1 = ws.getCell('A1');
  t1.value = title;
  t1.font = { bold: true, size: 12, name: 'Calibri' };
  t1.alignment = { horizontal: 'center', vertical: 'middle' };

  // Row 2 — yellow header band across every column
  ws.getRow(2).height = 30;
  headers.forEach((h, ci) => {
    const cell = ws.getRow(2).getCell(ci + 1);
    cell.value = h;
    cell.font = { bold: true, size: 10, name: 'Calibri' };
    cell.fill = solid(yellow);
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = allBdr(yellowBdr);
  });

  // Rows 3+ — plain bordered data, no zebra striping
  rows.forEach((rowData, idx) => {
    const row = ws.getRow(3 + idx);
    row.height = 16;
    rowData.forEach((v, ci) => {
      const cell = row.getCell(ci + 1);
      cell.value = v as any;
      cell.border = allBdr();
      cell.alignment = { horizontal: ci === 0 ? 'center' : 'left', vertical: 'middle' };
      cell.font = { size: 10, name: 'Calibri' };
    });
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
